import isNaN from 'lodash/isNaN';
import React, { useEffect, useState } from 'react';
import UI from '../../ui';
import { Instructions } from '../Instructions';
import { PackingMethodIdMap } from '../Mappings';
import { InboundStatus } from '../Status';
import { ValidationErrors } from '../Validations';
import PackingMethod from './PackingMethod.component';
import ShippingDropdown from './ShippingDropdown.component';
import WarehouseDropdown from './WarehouseDropdown.component';

const { WrappedInputTextWithLabel } = UI;

const InboundDetails = ({
	inbound = {},
	saveInbound,
	updateInstructions,
	showMobileView,
	isMobile,
	validations,
	updateValidation,
}) => {
	const [updatedInbound, setUpdatedInbound] = useState(inbound);
	const isEditable =
		inbound.status !== InboundStatus.Completed &&
		inbound.status !== InboundStatus.Cancelled;

	useEffect(() => {
		setUpdatedInbound(inbound);
	}, [inbound]);

	const validateInput = (name, value) => {
		const updatedValidations = validations;

		if (
			(name === 'purchaseOrderNumber' && value.length > 255) ||
			(name === 'reference' && value.length > 255)
		) {
			updatedValidations[name] = -1;
		} else {
			updatedValidations[name] = 1;
		}

		updateValidation(updatedValidations);
	};

	const onInputChange = e => {
		const { name, value } = e.target;

		saveInbound({ [name]: value });
		validateInput(name, value);
	};

	const saveDefaultInboundSelection = (name, value) => {
		const inboundDefaultValues = localStorage.getItem('inboundDefaultValues');
		const parsedInboundDefaultValues =
			inboundDefaultValues && inboundDefaultValues !== ''
				? JSON.parse(decodeURIComponent(inboundDefaultValues))
				: {};

		parsedInboundDefaultValues[name] = value;

		localStorage.setItem(
			'inboundDefaultValues',
			encodeURIComponent(JSON.stringify(parsedInboundDefaultValues))
		);
	};

	const updateInboundInstructions = (header, instructions) => {
		updateInstructions('inbound', header, instructions);
	};

	const onInputFocus = e => {
		const { name } = e.target;
		let instructions = '',
			header = '';

		if (name === 'purchaseOrderNumber') {
			header = Instructions.headers.inbound.purchaseOrder;
			instructions = Instructions.instructions.inbound.purchaseOrder;
		} else if (name === 'reference') {
			header = Instructions.headers.inbound.reference;
			instructions = Instructions.instructions.inbound.reference;
		}

		updateInboundInstructions(header, instructions);
	};

	const getPackagingType = packagingType => {
		if (!isNaN(parseInt(packagingType))) {
			return packagingType;
		} else {
			return packagingType === 'pallet'
				? PackingMethodIdMap.Pallet
				: PackingMethodIdMap.Boxes;
		}
	};

	const detailsClasses = `${
		showMobileView ? 'details-column-resized' : 'p-lg-6 details-column-full'
	} ${updatedInbound.status === InboundStatus.Processing ? 'processing' : ''}`;

	const editableInputs = (
		<div className={detailsClasses}>
			<WarehouseDropdown
				isMobile={isMobile}
				inboundWarehouseId={updatedInbound.warehouseId}
				inboundStatus={updatedInbound.status}
				onWarehouseChange={saveInbound}
				updateInboundInstructions={updateInboundInstructions}
				saveDefaultInboundSelection={saveDefaultInboundSelection}
				validations={validations}
				updateValidation={updateValidation}
			/>
			<WrappedInputTextWithLabel
				label="PO #"
				isInputInvalid={validations.purchaseOrderNumber === -1}
				showErrorBelow={isMobile}
				errorMessage={ValidationErrors.purchaseOrderMaxChar}
				uiElementConfig={{
					name: 'purchaseOrderNumber',
					value: updatedInbound.purchaseOrderNumber,
					placeholder: 'Optional',
					onChange: onInputChange,
					onFocus: onInputFocus,
					dataqa: 'ponumber',
				}}
			/>
			<WrappedInputTextWithLabel
				label="Reference"
				isInputInvalid={validations.reference === -1}
				showErrorBelow={isMobile}
				errorMessage={ValidationErrors.referenceMaxChar}
				uiElementConfig={{
					name: 'reference',
					value: updatedInbound.reference,
					placeholder: 'Optional',
					onChange: onInputChange,
					onFocus: onInputFocus,
					dataqa: 'reference',
				}}
			/>
		</div>
	);

	const nonEditableInputs = (
		<div className={detailsClasses}>
			<WarehouseDropdown
				isMobile={isMobile}
				inboundWarehouseId={updatedInbound.warehouseId}
				inboundStatus={updatedInbound.status}
				onWarehouseChange={saveInbound}
				updateInboundInstructions={updateInboundInstructions}
				saveDefaultInboundSelection={saveDefaultInboundSelection}
				validations={validations}
				updateValidation={updateValidation}
			/>
			<div className="read-only-label-container">
				<div className="read-only-label">PO #</div>
				<div className="read-only-text">
					{updatedInbound.purchaseOrderNumber}
				</div>
			</div>
			<div className="read-only-label-container">
				<div className="read-only-label">Reference</div>
				<div className="read-only-text">{updatedInbound.reference}</div>
			</div>
		</div>
	);

	return (
		<div className="common-inbound-details-inbound-details">
			<div className="inbound-details-header">Inbound Details</div>
			<div className="p-grid p-col-12 p-dir-col inbound-details-content">
				{isEditable && editableInputs}
				{!isEditable && nonEditableInputs}

				<div className={detailsClasses}>
					<PackingMethod
						packagingType={getPackagingType(updatedInbound.packagingType)}
						inboundStatus={updatedInbound.status}
						shippingMethod={updatedInbound.shippingMethod}
						onPackingMethodChange={saveInbound}
						updateInboundInstructions={updateInboundInstructions}
						saveDefaultInboundSelection={saveDefaultInboundSelection}
						isMobile={isMobile}
						validations={validations}
						updateValidation={updateValidation}
					/>
					<ShippingDropdown
						selectedShippingMethod={updatedInbound.shippingMethod}
						inboundStatus={updatedInbound.status}
						onShippingMethodChange={saveInbound}
						updateInboundInstructions={updateInboundInstructions}
						saveDefaultInboundSelection={saveDefaultInboundSelection}
						isMobile={isMobile}
						validations={validations}
						updateValidation={updateValidation}
					/>
				</div>
			</div>
		</div>
	);
};

export default InboundDetails;
